import type { ReactNode } from "react";
import type { BlockType, Point } from "@worktools/shared";
import { useUi, type Tool } from "../state/ui";
import { useBoard } from "../sync/BoardContext";
import { addBlockAt } from "../board/actions";
import { useMyGrants } from "../sync/hooks";
import {
  IconCard,
  IconCheck,
  IconCursor,
  IconDiagram,
  IconEraser,
  IconHand,
  IconMarker,
  IconNote,
  IconPen,
  IconTable,
  IconText,
  IconTimer,
} from "./icons";

const TOOLS: { tool: Tool; icon: ReactNode; title: string }[] = [
  { tool: "select", icon: <IconCursor />, title: "Auswählen (V)" },
  { tool: "pan", icon: <IconHand />, title: "Verschieben (H · Leertaste)" },
  { tool: "pen", icon: <IconPen />, title: "Stift (P)" },
  { tool: "marker", icon: <IconMarker />, title: "Marker" },
  { tool: "eraser", icon: <IconEraser />, title: "Radierer (E)" },
];

const BLOCKS: { type: BlockType; icon: ReactNode; title: string }[] = [
  { type: "checklist", icon: <IconCheck />, title: "Checkliste" },
  { type: "timer", icon: <IconTimer />, title: "Timer" },
  { type: "note", icon: <IconNote />, title: "Notizzettel" },
  { type: "text", icon: <IconText />, title: "Text" },
  { type: "card", icon: <IconCard />, title: "Karte" },
  { type: "table", icon: <IconTable />, title: "Tabelle" },
  { type: "diagram", icon: <IconDiagram />, title: "Diagramm" },
];

const PEN_COLORS = ["#0a0a0a", "#1f7a72", "#b4532a", "#3a5ba0", "#a63d5f", "#8a7320"];

export function Dock({ centerPoint }: { centerPoint: () => Point }) {
  const { doc, me } = useBoard();
  const grants = useMyGrants();
  const grantCount = Object.keys(grants).length;
  const tool = useUi((state) => state.tool);
  const setTool = useUi((state) => state.setTool);
  const penColor = useUi((state) => state.penColor);
  const setPen = useUi((state) => state.setPen);
  const setSelection = useUi((state) => state.setSelection);

  const insert = (type: BlockType) => {
    const id = addBlockAt(doc, type, centerPoint(), me.id);
    setSelection([id]);
    setTool("select");
  };

  return (
    <aside className="dock">
      {TOOLS.map((item) => (
        <button
          key={item.tool}
          className={`tool${tool === item.tool ? " tool--active" : ""}`}
          title={item.title}
          onClick={(event) => {
            setTool(item.tool);
            event.currentTarget.blur();
          }}
        >
          {item.icon}
        </button>
      ))}

      {(tool === "pen" || tool === "marker") && (
        <div className="dock__swatches">
          {PEN_COLORS.map((color) => (
            <button
              key={color}
              className={`swatch${penColor === color ? " swatch--active" : ""}`}
              style={{ background: color }}
              onClick={() => setPen({ color })}
            />
          ))}
        </div>
      )}

      <div className="dock__sep" />

      <button
        className={`tool tool--ai${tool === "grant" ? " tool--ai-active" : ""}`}
        title="Blöcke für deinen Claude freigeben — im Modus auf Blöcke klicken"
        onClick={(event) => {
          setTool(tool === "grant" ? "select" : "grant");
          setSelection([]);
          event.currentTarget.blur();
        }}
      >
        <img className="tool__logo" src="/claude-logo.svg" alt="Claude" />
        {grantCount > 0 && <span className="tool__badge">{grantCount}</span>}
      </button>

      <div className="dock__sep" />

      {BLOCKS.map((item) => (
        <button
          key={item.type}
          className="tool"
          title={`${item.title} — klicken oder auf die Fläche ziehen`}
          draggable
          onDragStart={(event) => {
            event.dataTransfer.setData("application/x-worktools-block", item.type);
            event.dataTransfer.effectAllowed = "copy";
          }}
          onClick={(event) => {
            insert(item.type);
            event.currentTarget.blur();
          }}
        >
          {item.icon}
        </button>
      ))}
    </aside>
  );
}
